// Third-party
import React, { useState } from 'react';
import classNames from 'classnames';

export type CollapsedSimpleTableRows = Array<{
  key?: string;
  parentKey?: string;
  elements: React.ReactNode[];
  className?: string;
}>;

type Props = {
  className?: string;
  data: {
    rows: CollapsedSimpleTableRows;
    headers: React.ReactNode[];
  };
  stickyHeaders?: boolean;
};

/**
 * Component to render a simple table with collapsible rows.
 * Rows can be collapsed based on a parent key.
 */
const CollapsibleSimpleTable = ({ data, className, stickyHeaders }: Props) => {
  const { rows, headers } = data;
  const [shownKeys, setShownKeys] = useState(new Set<string>());

  const toggleRow = (key: string) => {
    setShownKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const hasParentRows = rows.some(row => row.key && rows.some(r => r.parentKey === row.key));
  const bottomLegendColor = headers.length > 5;

  return (
    <table className={classNames('simple-table collapsible-simple-table', className, { 'sticky-headers': stickyHeaders })}>
      <thead>
        <tr>
          {hasParentRows && <th className="icon-cell" />}
          {headers.map((header, index) => (
            <th key={index} className={classNames({ 'bottom-legend-color': bottomLegendColor })}>
              {header}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {rows.map((row, idx) => {
          const isParent = row.key && rows.some(r => r.parentKey === row.key);
          const isChildShown = row.key && shownKeys.has(row.key);
          const isParentShown = row.parentKey && shownKeys.has(row.parentKey);

          return (
            <tr
              key={row.key ?? `row-${idx}`}
              className={classNames(row.className, {
                'row-parent': isParent,
                'row-hidden': row.parentKey && !isParentShown,
              })}
              onClick={() => {
                if (isParent && row.key) {
                  toggleRow(row.key);
                }
              }}
            >
              {/* Icon cell */}
              {hasParentRows && (
                <td className="icon-cell">
                  {isParent ? (
                    <i
                      className={classNames('icon-arrow-drop-down', {
                        'toggle-down': !isChildShown,
                        'toggle-up': isChildShown,
                      })}
                    />
                  ) : null}
                </td>
              )}

              {row.elements.map((el, i) => (
                <td key={i}>{el}</td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default CollapsibleSimpleTable;
